import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Cache user profiles to avoid repeated Firestore calls
  const userProfileCache = useRef(new Map());

  const fetchUserProfile = useCallback(async (uid) => {
    // Check cache first
    if (userProfileCache.has(uid)) {
      return userProfileCache.get(uid);
    }

    try {
      console.log('AuthContext: Fetching profile for user:', uid);
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        console.log('AuthContext: Profile found:', profileData);
        // Cache the profile data
        userProfileCache.set(uid, profileData);
        return profileData;
      } else {
        console.log('AuthContext: No profile found, will create one');
        return null;
      }
    } catch (error) {
      console.error('AuthContext: Error fetching user profile:', error);
      
      // Check if it's a Firestore connection error
      if (error.code === 'unavailable' || error.code === 'permission-denied' || error.message?.includes('transport errored')) {
        console.warn('AuthContext: Firestore connection failed, using auth user only');
        // Return a basic profile structure to prevent app crashes
        return {
          uid,
          groups: [],
          parties: [],
          createdAt: new Date(),
          firestoreError: true
        };
      }
      
      return null;
    }
  }, [userProfileCache]);

  const createUserProfile = useCallback(async (user) => {
    const newProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || 'User',
      photoURL: user.photoURL || null,
      bio: '',
      phoneNumber: '',
      preferences: {
        notifications: true,
        emailUpdates: true,
        privacy: 'private'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      groups: [],
      parties: [],
      isProfileComplete: false
    };

    try {
      console.log('AuthContext: Creating new profile:', newProfile);
      await setDoc(doc(db, 'users', user.uid), newProfile);
      console.log('AuthContext: User profile created successfully');
      return newProfile;
    } catch (error) {
      console.error('AuthContext: Error creating user profile:', error);
      throw error;
    }
  }, []);

  async function signInWithGoogle() {
    try {
      console.log('AuthContext: Starting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log('AuthContext: Google sign-in successful:', user);
      
      // Check if user profile exists in Firestore
      let profileData = await fetchUserProfile(user.uid);
      
      if (!profileData) {
        console.log('AuthContext: Creating new user profile...');
        // Create new user profile
        profileData = await createUserProfile(user);
        
        // Cache the new profile
        userProfileCache.set(user.uid, profileData);
        console.log('AuthContext: User profile created successfully');
      } else {
        console.log('AuthContext: User profile already exists');
        // Update last login time
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            lastLoginAt: new Date()
          });
        } catch (error) {
          console.warn('AuthContext: Could not update last login time:', error);
        }
      }
      
      // Set user immediately for better UX
      setCurrentUser({ ...user, ...profileData });
      setUserProfile(profileData);
      
      return user;
    } catch (error) {
      console.error('AuthContext: Error signing in with Google:', error);
      throw error;
    }
  }

  async function signOutUser() {
    try {
      console.log('AuthContext: Signing out user...');
      await signOut(auth);
      console.log('AuthContext: Sign out successful');
      
      // Clear cache and state immediately
      if (currentUser) {
        userProfileCache.delete(currentUser.uid);
      }
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('AuthContext: Error signing out:', error);
      throw error;
    }
  }

  async function updateUserProfile(updates) {
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      console.log('AuthContext: Updating profile with:', updates);

      const updateData = {
        ...updates,
        updatedAt: new Date()
      };

      // Skip Firebase Auth profile update to avoid getIdToken error
      // This ensures profile updates work reliably while avoiding Firebase Auth compatibility issues
      console.log('AuthContext: Skipping Auth profile update, updating Firestore only');
      
      // Update Firestore profile
      const userDocRef = doc(db, 'users', currentUser.uid);
      console.log('AuthContext: Updating Firestore document');
      await updateDoc(userDocRef, updateData);
      
      // Update cache
      const updatedProfile = { ...userProfile, ...updateData };
      userProfileCache.set(currentUser.uid, updatedProfile);
      
      // Update local state
      setCurrentUser(prev => ({ ...prev, ...updates }));
      setUserProfile(updatedProfile);

      console.log('AuthContext: Profile updated successfully');
    } catch (error) {
      console.error('AuthContext: Error updating profile:', error);
      throw error;
    }
  }

  // Refresh user profile from Firestore
  const refreshUserProfile = useCallback(async () => {
    if (!currentUser) return;

    try {
      console.log('AuthContext: Refreshing user profile...');
      const profileData = await fetchUserProfile(currentUser.uid);
      
      if (profileData) {
        setUserProfile(profileData);
        setCurrentUser(prev => ({ ...prev, ...profileData }));
        console.log('AuthContext: User profile refreshed successfully');
      }
    } catch (error) {
      console.error('AuthContext: Error refreshing user profile:', error);
    }
  }, [currentUser, fetchUserProfile]);

  useEffect(() => {
    console.log('AuthContext: Setting up auth state listener...');
    
    // Set loading to false faster for better UX
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 1000); // Reduced from 2 seconds to 1 second

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('AuthContext: Auth state changed:', user ? 'User signed in' : 'No user');
      
      if (user) {
        console.log('AuthContext: User details:', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        });
        
        // Set basic user info immediately for better UX
        setCurrentUser(user);
        
        // Fetch profile data in background (non-blocking)
        fetchUserProfile(user.uid).then(profileData => {
          if (profileData) {
            console.log('AuthContext: Profile loaded successfully');
            setUserProfile(profileData);
            setCurrentUser(prev => ({ ...prev, ...profileData }));
          } else {
            console.log('AuthContext: No profile found, creating one...');
            // Create profile if it doesn't exist
            createUserProfile(user).then(newProfile => {
              console.log('AuthContext: New profile created');
              setUserProfile(newProfile);
              setCurrentUser(prev => ({ ...prev, ...newProfile }));
              userProfileCache.set(user.uid, newProfile);
            }).catch(error => {
              console.error('AuthContext: Failed to create profile:', error);
            });
          }
        }).catch(error => {
          console.error('AuthContext: Background profile fetch error:', error);
        });
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      
      setLoading(false);
      clearTimeout(loadingTimeout);
      console.log('AuthContext: Loading state set to false');
    });

    return () => {
      unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, [fetchUserProfile, createUserProfile]);

  const value = {
    currentUser,
    userProfile,
    signInWithGoogle,
    signOutUser,
    updateUserProfile,
    refreshUserProfile,
    loading
  };

  console.log('AuthContext: Rendering with value:', {
    currentUser: currentUser ? 'User exists' : 'No user',
    userProfile: userProfile ? 'Profile loaded' : 'No profile',
    loading
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
